import React, { useState } from 'react';
import {
  Lock,
  Award,
  Briefcase,
  Users,
  TrendingUp,
  Shield,
  Target,
  Handshake,
  Smartphone,
  Globe,
  GraduationCap,
  Gem,
  BadgeDollarSign,
  Facebook,
  Instagram,
  Linkedin,
  ArrowLeft,
  Building2,
  Layers,
  Sparkles,
  Home,
  ExternalLink
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';

const Primes = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === '1020') {
      setIsAuthenticated(true);
      setError('');
      setPassword('');
    } else {
      setError('Password incorreta. Tente novamente.');
      setPassword('');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />

        <Card className="w-full max-w-md bg-zinc-900/80 border-gold/30 backdrop-blur-xl relative z-10">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mb-4">
              <Lock className="h-7 w-7 text-gold" />
            </div>
            <CardTitle className="text-3xl text-white heading-luxury">
              <span className="text-gold">Primes</span>
            </CardTitle>
            <p className="text-gray-400 mt-2 text-sm tracking-wide uppercase">
              Acesso Restrito
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-5 mt-4">
              <div>
                <Label htmlFor="projeto-password" className="text-white">
                  Password
                </Label>
                <Input
                  id="projeto-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite a password"
                  className="mt-2 bg-black/60 border-gold/30 text-white placeholder:text-gray-500 focus:border-gold"
                  data-testid="projeto-password-input"
                  autoFocus
                />
                {error && (
                  <p className="text-red-400 text-sm mt-2" data-testid="projeto-password-error">
                    {error}
                  </p>
                )}
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-gold to-gold-light hover:from-gold-light hover:to-gold text-black font-semibold py-6"
                data-testid="projeto-login-btn"
              >
                Aceder
              </Button>
              <a
                href="/"
                className="block text-center text-sm text-gray-400 hover:text-gold transition-colors"
                data-testid="projeto-back-home"
              >
                <ArrowLeft className="inline h-4 w-4 mr-1" />
                Voltar à página inicial
              </a>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="projeto-content">
      {/* Top bar */}
      <div className="sticky top-0 z-50 bg-black/90 backdrop-blur-md border-b border-gold/20">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <a
            href="/"
            className="flex items-center gap-2 text-gray-300 hover:text-gold transition-colors text-sm"
            data-testid="projeto-nav-home"
          >
            <Home className="h-4 w-4" />
            Início
          </a>
          <span className="text-xs text-gray-500 uppercase tracking-wider">
            Dossier de Recrutamento
          </span>
        </div>
      </div>

      {/* Hero */}
      <section className="relative py-24 overflow-hidden border-b border-gold/10">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-zinc-950 to-black" />
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/30 mb-6">
            <Sparkles className="h-4 w-4 text-gold" />
            <span className="text-gold text-xs font-medium tracking-[0.2em] uppercase">
              PREDIMED PRIME 204
            </span>
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold heading-luxury mb-6">
            <span className="text-white">A tua carreira </span>
            <span className="text-gold">mora aqui.</span>
          </h1>

          <div className="h-1 w-24 gold-gradient mx-auto mb-8" />

          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed font-light">
            Dossier de Recrutamento — uma apresentação do projeto PREDIMED PRIME,
            as ferramentas, comissões e condições que oferecemos a quem quer
            construir uma carreira sólida na mediação imobiliária.
          </p>
        </div>
      </section>

      {/* Quem Somos */}
      <Section
        number="01"
        title="Quem Somos"
        icon={<Building2 className="h-6 w-6 text-gold" />}
      >
        <p className="text-gray-300 leading-relaxed text-lg mb-8">
          A <span className="text-gold font-semibold">PREDIMED</span> é uma marca
          registada de mediação imobiliária, criada em 2004 por Miguel Ribeiro. A{' '}
          <span className="text-white font-semibold">PREDIMED PORTUGAL</span> é a
          empresa que, em Portugal, detém os direitos de exploração da marca. A{' '}
          <span className="text-white font-semibold">REDE PREDIMED</span> é o
          conjunto de todos os profissionais que trabalham com a marca.
        </p>
        <p className="text-gray-400 leading-relaxed mb-10 italic">
          Na PREDIMED o trabalho dos consultores imobiliários é devidamente
          reconhecido e remunerado.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <PillarCard
            icon={<Target className="h-5 w-5 text-gold" />}
            title="Visão"
            text="Prosseguimos a visão de que o negócio é feito pelos consultores imobiliários e são eles o nosso foco. Para isso, viabilizamos aos nossos associados um modelo de negócio sustentável, equilibrado e altamente rentável, assente no princípio de «máxima comissão para o consultor»."
          />
          <PillarCard
            icon={<Handshake className="h-5 w-5 text-gold" />}
            title="Missão"
            text="Prestar um serviço de excelência aos nossos clientes, satisfazendo as suas necessidades."
          />
          <PillarCard
            icon={<TrendingUp className="h-5 w-5 text-gold" />}
            title="Objetivo"
            text="Ser uma marca imobiliária de referência, presente em todo o território nacional, promovendo ativamente a credibilização da mediação imobiliária."
          />
          <PillarCard
            icon={<Layers className="h-5 w-5 text-gold" />}
            title="Estratégia"
            text="Fornecer aos nossos associados as melhores ferramentas do mercado e as melhores condições remuneratórias, para que prestem um serviço de excelência aos nossos clientes."
          />
        </div>
      </Section>

      {/* Associações */}
      <Section
        number="02"
        title="Associações"
        icon={<Shield className="h-6 w-6 text-gold" />}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <AssociationCard
            name="APEMIP"
            description="Parceiro-chave na fileira da construção e do imobiliário, contribuindo, pela sua ação, para encontrar as melhores soluções para todos os problemas que afetam o setor imobiliário, a montante e a jusante do próprio ato da transação imobiliária."
          />
          <AssociationCard
            name="ASMIP"
            description="Associação dos Mediadores Imobiliários de Portugal. Constituiu-se para dar aos Mediadores a liberdade de escolha ao aderir a uma associação da classe. De âmbito nacional, com uma nova mentalidade de prestação de serviços e liderança, sempre em favor dos seus associados."
          />
        </div>
      </Section>

      {/* Distinções */}
      <Section
        number="03"
        title="Distinções"
        icon={<Award className="h-6 w-6 text-gold" />}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            {
              title: 'Compromisso Pagamento Pontual',
              text:
                'Programa em que os fornecedores se comprometem a cumprir os prazos acordados, transmitir instruções claras e procedimentos internos relacionados com faturação.'
            },
            {
              title: 'CONFIO',
              text:
                'Selo que nasce da parceria de três entidades de elevada credibilidade em Portugal — representando a Internet, os consumidores e o comércio eletrónico — para garantir a credibilidade das lojas online e a segurança dos consumidores.'
            },
            {
              title: 'Portugal Sou Eu',
              text:
                'Programa que visa a dinamização e valorização da oferta nacional com assinalável incorporação nacional, promovendo um consumo informado, através de uma marca ativa e identitária da produção nacional.'
            },
            {
              title: 'Salão Imobiliário de Portugal (2011)',
              text:
                'A PREDIMED foi distinguida com um prémio no Salão Imobiliário de Portugal com o produto «Arrendamento Seguro».'
            },
            {
              title: 'Melhor Agência 2023 — Região Centro',
              text:
                'A PREDIMED foi distinguida em 2023 com os Prémios Aproximo — IMOVIRTUAL.'
            }
          ].map((d, i) => (
            <div
              key={i}
              className="p-6 rounded-2xl bg-zinc-900/60 border border-gold/20 hover:border-gold/60 transition-all duration-500"
              data-testid={`projeto-distinction-${i}`}
            >
              <Award className="h-8 w-8 text-gold mb-3" />
              <h4 className="text-white font-semibold text-lg mb-2">{d.title}</h4>
              <p className="text-gray-400 text-sm leading-relaxed">{d.text}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* O que oferecemos */}
      <Section
        number="04"
        title="O Que Oferecemos"
        icon={<Gem className="h-6 w-6 text-gold" />}
      >
        <p className="text-gray-300 leading-relaxed text-lg mb-6">
          Para além das <span className="text-gold font-semibold">mais altas comissões do mercado</span>,
          a PREDIMED oferece uma vasta gama de ferramentas de trabalho e exporta
          todos os imóveis em comercialização para os principais portais
          nacionais e internacionais.
        </p>
        <p className="text-gray-400 leading-relaxed">
          Todos os consultores PREDIMED dispõem ainda de formação permanente,
          apoio à sua atividade comercial, apoio jurídico através de parcerias
          externas, processual, administrativo e contabilístico.
        </p>
      </Section>

      {/* Ferramentas de Trabalho */}
      <Section
        number="05"
        title="Ferramentas de Trabalho"
        icon={<Briefcase className="h-6 w-6 text-gold" />}
      >
        <div className="space-y-6">
          <ToolCard num="1" title="CRM PREDIMED" icon={<Layers className="h-5 w-5 text-gold" />}>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li>• Otimização máxima de recursos com mobilidade total (PC, tablet e smartphone)</li>
              <li>• Divulgação dos imóveis adaptada a todos os suportes, incluindo dos potenciais clientes</li>
              <li>• Partilha de imóveis por toda a rede PREDIMED</li>
              <li>• Presença nos principais portais imobiliários nacionais e internacionais</li>
              <li>• Notificações automáticas direcionadas ao angariador</li>
              <li>• Partilha direta nas redes sociais</li>
              <li>• Alimenta a aplicação PREDIMED (iOS & Android)</li>
            </ul>
          </ToolCard>

          <ToolCard num="2" title="Principais Plataformas Nacionais" icon={<Globe className="h-5 w-5 text-gold" />}>
            <div className="flex flex-wrap gap-2">
              {[
                'olx.com',
                'imovirtual.com',
                'custojusto.com',
                'mitula.com',
                'supercasa.pt',
                'donkiz.com',
                'trovit.com',
                'casascm.com',
                'casasapo.pt',
                'bpiexpressoimobiliario.pt',
                'casayes.pt',
                'properstar.pt',
                'kippertree.com'
              ].map((p) => (
                <span
                  key={p}
                  className="px-3 py-1 rounded-full bg-black/60 border border-gold/20 text-gold text-xs tracking-wide"
                >
                  {p}
                </span>
              ))}
            </div>
          </ToolCard>

          <ToolCard num="3" title="Plataformas Internacionais" icon={<Globe className="h-5 w-5 text-gold" />}>
            <p className="text-gray-400 text-sm leading-relaxed">
              <span className="text-white font-semibold">listglobally.com</span> — Através
              deste serviço, exportamos os imóveis para{' '}
              <span className="text-gold font-semibold">mais de 100 portais</span> em
              <span className="text-gold font-semibold"> mais de 60 países</span>.
            </p>
          </ToolCard>

          <ToolCard num="4" title="REATIA — MetaSearch" icon={<Sparkles className="h-5 w-5 text-gold" />}>
            <p className="text-gray-400 text-sm mb-3 leading-relaxed">
              Ferramenta de MetaSearch na qual a PREDIMED foi pioneira em Portugal.
              Reduz a escassos minutos as horas que anteriormente eram gastas na
              pesquisa de novos imóveis.
            </p>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li>• <span className="text-white">Procura Inteligente</span> — todas as propriedades no mercado via IA e metasearch</li>
              <li>• <span className="text-white">Sistema de Alertas</span> — sê mais rápido que a concorrência</li>
              <li>• <span className="text-white">Tempo Real</span> — tendências locais e nacionais + avaliação de mercado de cada imóvel</li>
            </ul>
          </ToolCard>

          <ToolCard num="5" title="APP PREDIMED" icon={<Smartphone className="h-5 w-5 text-gold" />}>
            <p className="text-gray-400 text-sm leading-relaxed">
              Aplicação iOS & Android. Destinada a clientes finais e excelente
              ferramenta para os consultores — mostra os imóveis inseridos na rede
              PREDIMED bem como as informações dos nossos consultores.
            </p>
          </ToolCard>

          <ToolCard num="6" title="APP PP360" icon={<Smartphone className="h-5 w-5 text-gold" />}>
            <p className="text-gray-400 text-sm leading-relaxed">
              Acesso ao CRM a partir de qualquer smartphone ou tablet — inserir
              imóveis, consultar agenda, gerir o dia-a-dia em mobilidade.
            </p>
          </ToolCard>

          <ToolCard num="7" title="Marketing Digital" icon={<Sparkles className="h-5 w-5 text-gold" />}>
            <p className="text-gray-400 text-sm leading-relaxed">
              O Departamento de Marketing Digital produz conteúdos de imagem para
              placas, flyers, cartões de visita, lonas, pastas, capas e outros
              elementos de marketing corporativo e pessoal.
            </p>
          </ToolCard>

          <ToolCard num="8" title="Website Próprio" icon={<Globe className="h-5 w-5 text-gold" />}>
            <p className="text-gray-400 text-sm leading-relaxed">
              Plataforma para gerir o site da agência com layout standard PREDIMED.
              Inserir dados, imóveis e caixas de correio eletrónico. Simples,
              prático e rápido.
            </p>
          </ToolCard>

          <ToolCard num="9" title="Formação — Academia PREDIMED" icon={<GraduationCap className="h-5 w-5 text-gold" />}>
            <p className="text-gray-400 text-sm leading-relaxed">
              A chave para o sucesso. Ações online e presenciais, gratuitas, com
              as melhores formações do mercado para todos os consultores.
            </p>
          </ToolCard>

          <ToolCard num="10" title="Segmento de Luxo — PREDIMED PRESTIGE" icon={<Gem className="h-5 w-5 text-gold" />}>
            <p className="text-gray-400 text-sm leading-relaxed mb-3">
              O segmento dos Imóveis de Excelência tem uma plataforma específica.
            </p>
            <a
              href="https://predimedprestige.pt"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-gold hover:text-gold-light text-sm font-medium"
              data-testid="projeto-prestige-link"
            >
              predimedprestige.pt
              <ExternalLink className="h-4 w-4" />
            </a>
          </ToolCard>

          <ToolCard num="11" title="Apoios" icon={<Handshake className="h-5 w-5 text-gold" />}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                'Jurídico',
                'Comercial',
                'Técnico',
                'Contabilístico',
                'Administrativo / Processual',
                'Marketing'
              ].map((a) => (
                <div
                  key={a}
                  className="px-3 py-2 rounded-lg bg-black/60 border border-gold/20 text-center text-sm text-gray-300"
                >
                  Apoio <span className="text-gold">{a}</span>
                </div>
              ))}
            </div>
          </ToolCard>
        </div>
      </Section>

      {/* Comissões */}
      <Section
        number="06"
        title="Comissões"
        icon={<BadgeDollarSign className="h-6 w-6 text-gold" />}
      >
        <h3 className="text-2xl font-bold text-white mb-6 heading-luxury">
          Comissões <span className="text-gold">Diretas</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <CommissionDirectCard
            title="Angariação"
            rate="80%"
            breakdown={[
              { label: 'Comissão paga pelo cliente', value: '10.000,00 €' },
              { label: 'Comissão pela angariação', value: '4.000,00 €' }
            ]}
            total="4.000,00 €"
          />
          <CommissionDirectCard
            title="Venda"
            rate="80%"
            breakdown={[
              { label: 'Comissão paga pelo cliente', value: '10.000,00 €' },
              { label: 'Comissão pela venda', value: '4.000,00 €' }
            ]}
            total="4.000,00 €"
          />
          <CommissionDirectCard
            title="Pleno"
            rate="80% + 80%"
            highlighted
            breakdown={[
              { label: 'Comissão paga pelo cliente', value: '10.000,00 €' },
              { label: 'Pela angariação', value: '4.000,00 €' },
              { label: 'Pela venda', value: '4.000,00 €' }
            ]}
            total="8.000,00 €"
          />
        </div>

        <h3 className="text-2xl font-bold text-white mb-6 heading-luxury">
          Comissões de <span className="text-gold">Rede</span>
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { grade: '1º Grau', pct: '10%' },
            { grade: '2º Grau', pct: '5%' },
            { grade: '3º Grau', pct: '2,5%' },
            { grade: '4º Grau', pct: '1,5%' },
            { grade: '5º Grau', pct: '1%' }
          ].map((g, i) => (
            <div
              key={g.grade}
              className="p-6 rounded-2xl bg-gradient-to-br from-zinc-900 to-black border border-gold/30 text-center hover:border-gold/70 transition-all"
              data-testid={`projeto-network-grade-${i + 1}`}
            >
              <div className="text-xs tracking-[0.2em] uppercase text-gray-400 mb-2">
                {g.grade}
              </div>
              <div className="text-3xl md:text-4xl font-bold text-gold heading-luxury">
                {g.pct}
              </div>
            </div>
          ))}
        </div>

        <p className="text-gray-500 text-sm italic mt-6">
          * O método de cálculo do 2º ao 5º grau é idêntico; apenas a percentagem
          aplicada é diferente.
        </p>
      </Section>

      {/* Investimento */}
      <Section
        number="07"
        title="Investimento"
        icon={<TrendingUp className="h-6 w-6 text-gold" />}
      >
        <div className="p-10 md:p-12 rounded-3xl bg-gradient-to-br from-gold/10 via-zinc-900 to-black border border-gold/40 text-center luxury-shadow">
          <div className="text-xs tracking-[0.3em] uppercase text-gold mb-4">
            Fee Anual
          </div>
          <div className="text-5xl md:text-6xl font-bold text-white heading-luxury mb-3">
            1.250,00 € <span className="text-gold text-3xl">+ IVA</span>
          </div>
          <p className="text-gray-400 leading-relaxed max-w-xl mx-auto">
            O Consultor PREDIMED paga o fee anual de <span className="text-white">1.250,00 € + IVA</span>,
            a descontar nas comissões de venda e de rede.
          </p>
        </div>
      </Section>

      {/* Outros Benefícios */}
      <Section
        number="08"
        title="Outros Benefícios"
        icon={<Sparkles className="h-6 w-6 text-gold" />}
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            'Financiamento Bancário',
            'Seguros',
            'Certificação Energética',
            'Obras'
          ].map((b, i) => (
            <div
              key={b}
              className="p-6 rounded-2xl bg-zinc-900/60 border border-gold/20 hover:border-gold/60 transition-all text-center"
              data-testid={`projeto-benefit-${i}`}
            >
              <Sparkles className="h-6 w-6 text-gold mx-auto mb-3" />
              <div className="text-white font-medium text-sm">{b}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Redes Sociais + CTA final */}
      <section className="py-24 border-t border-gold/20 bg-gradient-to-b from-black to-zinc-950">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold heading-luxury mb-4">
            <span className="text-white">Vem </span>
            <span className="text-gold">trabalhar connosco.</span>
          </h2>
          <p className="text-gray-400 mb-10">
            PREDIMED PRIME — Mediação Imobiliária, Lda. &nbsp;·&nbsp; Licença AMI 22553
          </p>

          <div className="flex items-center justify-center gap-4 mb-10">
            <a
              href="https://www.facebook.com/PredimedMaster"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 rounded-full bg-zinc-900 border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-black transition-all"
              data-testid="projeto-social-facebook"
              aria-label="Facebook"
            >
              <Facebook className="h-5 w-5" />
            </a>
            <a
              href="https://www.instagram.com/predimed_portugal"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 rounded-full bg-zinc-900 border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-black transition-all"
              data-testid="projeto-social-instagram"
              aria-label="Instagram"
            >
              <Instagram className="h-5 w-5" />
            </a>
            <a
              href="https://www.linkedin.com/company/predimed-portugal"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 rounded-full bg-zinc-900 border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-black transition-all"
              data-testid="projeto-social-linkedin"
              aria-label="LinkedIn"
            >
              <Linkedin className="h-5 w-5" />
            </a>
          </div>

          <Button
            asChild
            size="lg"
            className="bg-gradient-to-r from-gold to-gold-light hover:from-gold-light hover:to-gold text-black font-semibold px-8 py-6 text-lg"
            data-testid="projeto-cta-contact"
          >
            <a href="tel:+351934608020">
              <Users className="mr-2 h-5 w-5" />
              Falar com Miguel Ângelo
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};

/* ----------------- Sub-components ----------------- */

const Section = ({ number, title, icon, children }) => (
  <section
    className="py-20 border-b border-gold/10"
    data-testid={`projeto-section-${number}`}
  >
    <div className="max-w-5xl mx-auto px-4">
      <div className="flex items-center gap-4 mb-10">
        <div className="text-gold text-sm font-mono tracking-[0.3em]">{number}</div>
        <div className="h-px flex-1 bg-gold/30" />
        <div className="w-11 h-11 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
          {icon}
        </div>
      </div>
      <h2 className="text-4xl md:text-5xl font-bold heading-luxury text-white mb-10">
        {title}
      </h2>
      {children}
    </div>
  </section>
);

const PillarCard = ({ icon, title, text }) => (
  <div className="p-6 rounded-2xl bg-zinc-900/60 border border-gold/20 hover:border-gold/60 transition-all duration-500">
    <div className="flex items-center gap-3 mb-3">
      {icon}
      <h3 className="text-xl font-bold text-white heading-luxury">{title}</h3>
    </div>
    <p className="text-gray-400 leading-relaxed text-sm">{text}</p>
  </div>
);

const AssociationCard = ({ name, description }) => (
  <div className="p-8 rounded-2xl bg-gradient-to-br from-zinc-900 to-black border border-gold/20 hover:border-gold/60 transition-all duration-500">
    <div className="text-3xl font-bold text-gold heading-luxury mb-4">{name}</div>
    <p className="text-gray-400 leading-relaxed text-sm">{description}</p>
  </div>
);

const ToolCard = ({ num, title, icon, children }) => (
  <div
    className="p-6 md:p-8 rounded-2xl bg-zinc-900/60 border border-gold/20 hover:border-gold/50 transition-all duration-500"
    data-testid={`projeto-tool-${num}`}
  >
    <div className="flex items-start gap-4 mb-4">
      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1">
        <div className="text-xs text-gold tracking-[0.2em] uppercase mb-1">
          Ferramenta {num}
        </div>
        <h3 className="text-xl font-bold text-white heading-luxury">{title}</h3>
      </div>
    </div>
    <div className="pl-0 md:pl-16">{children}</div>
  </div>
);

const CommissionDirectCard = ({ title, rate, breakdown, total, highlighted }) => (
  <div
    className={`p-6 rounded-2xl transition-all duration-500 ${
      highlighted
        ? 'bg-gradient-to-br from-gold/20 via-zinc-900 to-black border-2 border-gold/60 luxury-shadow'
        : 'bg-zinc-900/60 border border-gold/20 hover:border-gold/50'
    }`}
  >
    <div className="text-xs tracking-[0.2em] uppercase text-gray-400 mb-1">{title}</div>
    <div className="text-3xl font-bold text-gold heading-luxury mb-6">{rate}</div>

    <div className="space-y-2 text-sm mb-6">
      {breakdown.map((row) => (
        <div key={row.label} className="flex justify-between gap-3">
          <span className="text-gray-400">{row.label}</span>
          <span className="text-white font-medium">{row.value}</span>
        </div>
      ))}
    </div>

    <div className="pt-4 border-t border-gold/20">
      <div className="text-xs uppercase tracking-wider text-gray-400 mb-1">
        Comissão total
      </div>
      <div className="text-2xl font-bold text-white heading-luxury">{total}</div>
    </div>
  </div>
);

export default Primes;
